import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, FileText, Image as ImgIcon, Mail, Camera, ClipboardCheck, Receipt, Calendar as CalIcon, Wrench } from "lucide-react";

/**
 * TimelineGrid
 *
 * A calendar-style month-by-month visualisation of every timeline item for
 * a case. Renders a 7-column grid (Mon → Sun by Australian convention).
 * Each day cell shows up to 3 chips for items on that date plus a "+N more"
 * pill when overflow.
 *
 * Props:
 *   items: array of { id, kind, title, description, date (YYYY-MM-DD), doc_type, document_id }
 *   onItemClick?: (item) => void
 *
 * Intentionally a static visualisation, drag-and-drop date editing lives
 * in TimelineRuler. This view is for at-a-glance scanning when prepping for
 * a tribunal.
 */
export default function TimelineGrid({ items = [], onItemClick, caseColors = null, showCaseLegend = false }) {
  const today = new Date();
  const [cursor, setCursor] = useState(() => {
    // Default the cursor to the most recent item's month so the user doesn't
    // land on an empty current month for a 6-month-old dispute. Dates may
    // arrive as YYYY-MM-DD or full ISO timestamps so we slice to the date
    // part before building a Date object.
    const dated = items.filter((i) => i.date).sort((a, b) => b.date.localeCompare(a.date));
    if (dated.length) {
      const d = new Date(dated[0].date.slice(0, 10) + "T00:00:00");
      if (!isNaN(d.getTime())) {
        return { year: d.getFullYear(), month: d.getMonth() };
      }
    }
    return { year: today.getFullYear(), month: today.getMonth() };
  });

  // Bucket items by ISO date string for O(1) cell lookups
  const itemsByDate = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      if (!it.date) continue;
      const k = it.date.slice(0, 10);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(it);
    }
    return m;
  }, [items]);

  // Build the 6x7 grid for this month (Mon-start week)
  const cells = useMemo(() => buildMonthCells(cursor.year, cursor.month), [cursor]);

  const monthLabel = new Date(cursor.year, cursor.month, 1).toLocaleString("en-AU", {
    month: "long",
    year: "numeric",
  });

  const shiftMonth = (delta) => {
    setCursor((c) => {
      const d = new Date(c.year, c.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  const jumpToToday = () => {
    setCursor({ year: today.getFullYear(), month: today.getMonth() });
  };

  // Year strip showing which months have activity, quick navigation
  const monthsWithActivity = useMemo(() => {
    const s = new Set();
    items.forEach((it) => {
      if (it.date) s.add(it.date.slice(0, 7)); // YYYY-MM
    });
    return s;
  }, [items]);

  return (
    <div data-testid="timeline-grid">
      {/* Month header + nav */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => shiftMonth(-1)}
            className="btn-ghost p-1.5"
            data-testid="grid-prev-month"
            aria-label="Previous month"
          >
            <ChevronLeft size={16} strokeWidth={1.5} />
          </button>
          <h3 className="font-serif text-xl text-[#0D2B52] min-w-[180px] text-center">{monthLabel}</h3>
          <button
            type="button"
            onClick={() => shiftMonth(1)}
            className="btn-ghost p-1.5"
            data-testid="grid-next-month"
            aria-label="Next month"
          >
            <ChevronRight size={16} strokeWidth={1.5} />
          </button>
        </div>
        <button
          type="button"
          onClick={jumpToToday}
          className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#1B6C62] hover:underline"
          data-testid="grid-jump-today"
        >
          Today
        </button>
      </div>

      {/* Day-of-week header, Aussie convention starts on Monday */}
      <div className="grid grid-cols-7 gap-px bg-[#E5E4DE] border border-[#E5E4DE] mb-px">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div
            key={d}
            className="bg-[#FAF9F6] text-center py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B7280]"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-px bg-[#E5E4DE] border border-[#E5E4DE] border-t-0">
        {cells.map((cell) => {
          const dateKey = cell.iso;
          const cellItems = dateKey ? itemsByDate.get(dateKey) || [] : [];
          const isToday =
            cell.iso === today.toISOString().slice(0, 10) && cell.inMonth;
          const isOtherMonth = !cell.inMonth;
          return (
            <div
              key={cell.key}
              className={`bg-white min-h-[96px] p-1.5 relative ${
                isOtherMonth ? "opacity-40" : ""
              }`}
              data-testid={`grid-cell-${dateKey || cell.key}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`font-mono text-[11px] ${
                    isToday
                      ? "bg-[#1B6C62] text-white px-1.5 py-px"
                      : "text-[#1A1A1A]"
                  }`}
                >
                  {cell.day}
                </span>
                {cellItems.length > 3 && (
                  <span className="font-mono text-[9px] text-[#6B7280]">+{cellItems.length - 3}</span>
                )}
              </div>
              <div className="space-y-0.5">
                {cellItems.slice(0, 3).map((it) => (
                  <DayChip key={it.id} item={it} onClick={onItemClick} caseColors={caseColors} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Activity strip, shows which months in the case have items */}
      <ActivityStrip
        monthsWithActivity={monthsWithActivity}
        cursor={cursor}
        onJump={(y, m) => setCursor({ year: y, month: m })}
      />

      {/* Legend */}
      {showCaseLegend && caseColors ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 text-[11px] text-[#6B7280] font-mono" data-testid="grid-case-legend">
          {[...caseColors.entries()].map(([cid, col]) => {
            const sample = items.find((it) => it.case_id === cid);
            const label = sample?.case_title || cid.slice(0, 6);
            return (
              <span key={cid} className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5" style={{ background: col.stripe }} />
                <span className="text-[#0D2B52]">{label}</span>
              </span>
            );
          })}
          <span className="italic font-serif text-[#9CA3AF]">click a chip to open the case</span>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 text-[11px] text-[#6B7280] font-mono">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 bg-[#1B6C62]" /> Document
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 bg-[#8A6D3B]" /> Event
          </span>
          <span className="italic font-serif text-[#9CA3AF]">click a chip to jump to it</span>
        </div>
      )}
    </div>
  );
}

/* ---------- Subcomponents ---------- */

function DayChip({ item, onClick, caseColors }) {
  const Icon = iconFor(item);
  // When the parent passes a caseColors map, colour-code chips by case so
  // a renter with multiple tenancies can tell them apart on one grid.
  const caseCol = caseColors && item.case_id ? caseColors.get(item.case_id) : null;
  const inlineStyle = caseCol
    ? {
        background: caseCol.bg,
        color: caseCol.ink,
        borderLeft: `2px solid ${caseCol.stripe}`,
      }
    : undefined;
  const fallbackTone =
    item.kind === "document"
      ? "bg-[#E6F5F2] text-[#0D2B52] border-l-2 border-[#1B6C62]"
      : "bg-[#FAF6E8] text-[#0D2B52] border-l-2 border-[#8A6D3B]";
  return (
    <button
      type="button"
      onClick={() => onClick && onClick(item)}
      title={
        item.case_title ? `${item.case_title}: ${item.title}` : item.title
      }
      className={`w-full text-left px-1.5 py-0.5 ${
        caseCol ? "" : fallbackTone
      } hover:opacity-80 flex items-center gap-1`}
      style={inlineStyle}
      data-testid={`grid-chip-${item.id}`}
    >
      <Icon size={9} strokeWidth={2} className="shrink-0" />
      <span className="text-[10.5px] leading-tight truncate">{item.title}</span>
    </button>
  );
}

function ActivityStrip({ monthsWithActivity, cursor, onJump }) {
  // Build a strip covering 18 months centred-ish around cursor.
  const months = [];
  const start = new Date(cursor.year, cursor.month - 11, 1);
  for (let i = 0; i < 18; i++) {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    months.push({
      key,
      y: d.getFullYear(),
      m: d.getMonth(),
      label: d.toLocaleString("en-AU", { month: "short" }),
      active: monthsWithActivity.has(key),
      isCurrent: d.getFullYear() === cursor.year && d.getMonth() === cursor.month,
    });
  }
  return (
    <div className="mt-4 pt-3 border-t border-[#E5E4DE]" data-testid="grid-activity-strip">
      <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B7280] mb-2">
        Months with activity
      </div>
      <div className="flex gap-1 overflow-x-auto pb-1">
        {months.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => onJump(m.y, m.m)}
            className={`shrink-0 px-2 py-1 font-mono text-[10px] border ${
              m.isCurrent
                ? "border-[#1B6C62] bg-[#1B6C62] text-white"
                : m.active
                ? "border-[#1B6C62]/40 bg-[#E6F5F2] text-[#0D2B52] hover:bg-[#1B6C62]/15"
                : "border-[#E5E4DE] bg-[#FAF9F6] text-[#9CA3AF] hover:bg-white"
            }`}
            data-testid={`grid-strip-${m.key}`}
          >
            {m.label} {String(m.y).slice(2)}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- Helpers ---------- */

function iconFor(item) {
  if (item.kind === "event") return CalIcon;
  switch ((item.doc_type || "").toLowerCase()) {
    case "photo":
    case "inspection_photo":
      return Camera;
    case "email_reply":
    case "email":
      return Mail;
    case "lease":
    case "bond_receipt":
    case "letter":
      return FileText;
    case "receipt":
    case "invoice":
      return Receipt;
    case "inspection":
      return ClipboardCheck;
    case "repair":
      return Wrench;
    default:
      return ImgIcon;
  }
}

function buildMonthCells(year, month) {
  // Mon-start week
  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // JS Sunday=0..Saturday=6; we want Monday=0..Sunday=6
  const leadingBlanks = (firstOfMonth.getDay() + 6) % 7;
  const cells = [];
  // Trailing days of previous month
  const prevDays = new Date(year, month, 0).getDate();
  for (let i = leadingBlanks - 1; i >= 0; i--) {
    const day = prevDays - i;
    const d = new Date(year, month - 1, day);
    cells.push({
      key: `pre-${day}`,
      day,
      inMonth: false,
      iso: toIso(d),
    });
  }
  // Current month days
  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, month, day);
    cells.push({
      key: `cur-${day}`,
      day,
      inMonth: true,
      iso: toIso(d),
    });
  }
  // Trailing days from next month to fill 6 rows (42 cells)
  let nextDay = 1;
  while (cells.length < 42) {
    const d = new Date(year, month + 1, nextDay);
    cells.push({
      key: `nxt-${nextDay}`,
      day: nextDay,
      inMonth: false,
      iso: toIso(d),
    });
    nextDay++;
  }
  return cells;
}

function toIso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
